import React, { useState, useEffect, useContext } from "react";
import {
  Box,
  Button,
  Modal,
  Typography,
  Alert,
  InputAdornment,
  TextField,
  TablePagination
} from "@mui/material";
import { CirclePlus, Search, Upload } from "lucide-react";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import DataTable from "../../components/DataTable";
import {
  getProducts,
  createProduct,
  updateProduct,
  deleteProduct,
  getProductGroup,
  bulkUploadProduct
} from "../../api/Product/api";
import { AuthContext } from "../../contexts/AuthContext";
import usePermissions from "./hooks/usePermissions";
import ProductForm from "../../components/shared/forms/ProductForm";
import DeleteConfirmationModal from "../../components/shared/modals/DeleteConfirmationModal";
import Toast from "../../components/shared/Toast";
import { showSuccessToast, showErrorToast } from "../../utils/toastUtils";
import UploadModal from "../../components/UploadModal";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 900,
  bgcolor: "background.paper",
  boxShadow: 24,
  borderRadius: 1,
  p: 0,
};

const ProductList = () => {
  const {
    loading: loadingPermissions,
    error: permissionsError,
    can,
  } = usePermissions();
  const { user } = useContext(AuthContext);
  const [products, setProducts] = useState([]);
  const [productGroups, setProductGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [openUploadModal, setOpenUploadModal] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);

  useEffect(() => {
    if (!loadingPermissions && can.viewProduct()) {
      fetchProducts();
    }
  }, [loadingPermissions]);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const response = await getProducts({
        page: page + 1,
        limit: rowsPerPage,
        search: searchQuery,
      });
      if (response?.data) {
        const transformedData = response.data.map((product) => ({
          id: product.id,
          code: product.code,
          sku: product.sku,
          name: product.name,
          price: product.price,
          productGroup: product.product_group,
          productGroupName: product.product_group_name,
          classification: product.classification,
          strategy: product.strategy,
          leadTime: product.lead_time,
          coverageProfile: product.coverage_profile,
          planningCycle: product.planning_cycle,
          mrpCollector: product.mrp_collector,
          roundingValue: product.rounding_value,
          minOrderQuantity: product.min_order_quantity,
          maxOrderQuantity: product.max_order_quantity,
          category: product.category,
          categoryName: product.category_name,
          subCategory: product.sub_category,
          subCategoryName: product.sub_category_name,
        }));
        setProducts(transformedData);
        setTotal(response.total);
      }
      setError(null);
    } catch (err) {
      console.error("Error fetching products:", err);
      setError("Failed to load products. Please try again later.");
      setProducts([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, [page, rowsPerPage, searchQuery]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const transformProductData = (data) => {
    return {
      sku: data.sku,
      code: data.code,
      name: data.name,
      price: data.price,
      product_group: data.product_group,
      classification: data.classification,
      strategy: data.strategy,
      lead_time: data.lead_time,
      coverage_profile: data.coverage_profile,
      planning_cycle: data.planning_cycle,
      mrp_collector: data.mrp_collector,
      rounding_value: data.rounding_value,
      min_order_quantity: data.min_order_quantity,
      max_order_quantity: data.max_order_quantity,
      category: data.category,
      sub_category: data.sub_category
    };
  };

  const handleCreateProduct = async (data) => {
    if (!can.createProduct()) {
      setError("You do not have permission to add products");
      return;
    }

    try {
      const transformedData = transformProductData(data);
      await createProduct(transformedData);
      showSuccessToast("Product created successfully");
      await fetchProducts();
      setOpenModal(false);
      setError(null);
    } catch (err) {
      console.error("Error creating product:", err);
      showErrorToast("Failed to create product. Please try again.");
      setError("Failed to create product. Please try again.");
    }
  };

  const handleUpdateProduct = async (id, data) => {
    if (!can.editProduct()) {
      setError("You do not have permission to modify products");
      return;
    }

    try {
      const transformedData = transformProductData(data);
      await updateProduct(id, transformedData);
      await fetchProducts();
      showSuccessToast("Product updated successfully");
      setOpenModal(false);
      setSelectedProduct(null);
      setError(null);
    } catch (err) {
      console.error("Error updating product:", err.response.data);
      showErrorToast(err?.response?.data?.error?.message);
      setError("Failed to update product. Please try again.");
    }
  };

  const handleDeleteProduct = async () => {
    if (!can.deleteProduct()) {
      setError("You do not have permission to delete products");
      return;
    }

    try {
      await deleteProduct(productToDelete.id);
      await fetchProducts();
      setDeleteModalOpen(false);
      setProductToDelete(null);
      setError(null);
      showSuccessToast("Product deleted successfully");
    } catch (err) {
      console.error("Error deleting product:", err);
      setError("Failed to delete product. Please try again.");
      showErrorToast("Failed to delete product. Please try again.");
    }
  };

  const filteredProducts = products.filter((product) =>
    Object.values(product).some(
      (value) =>
        value &&
        value.toString().toLowerCase().includes(searchQuery.toLowerCase())
    )
  );

  const columns = [
    { id: "code", label: "Code" },
    { id: "sku", label: "SKU" },
    { id: "name", label: "Name" },
    { id: "price", label: "Price (KES)" },
    { id: "productGroupName", label: "Product Group" },
    { id: "classification", label: "classification"},
    { id: "strategy", label: "Strategy"},
    { id: "leadTime", label: "Lead Time"},
    { id: "coverageProfile", label: "Coverage Profile"},
    { id: "planningCycle", label: "Planning Cycle"},
    { id: "mrpCollector", label: "Mrp Collector"},
    { id: "roundingValue", label: "Rounding Value"},
    { id: "minOrderQuantity", label: "Min Order Quantity"},
    { id: "maxOrderQuantity", label: "Max Order Quantity"},
    { id: "categoryName", label: "Category"},
    { id: "subCategoryName", label: "Sub Category"},
  ];

  const handleSubmit = (data) => {
    if (selectedProduct) {
      handleUpdateProduct(selectedProduct.id, data);
    } else {
      handleCreateProduct(data);
    }
  };

  const handleFileChange = (e) => {
    setUploadFile(e.target.files[0]);
  };

  const handleCSVUpload = async () => {
    if (!uploadFile) {
      showErrorToast("Please select a CSV file", "Validation Error");
      return;
    }
    try {
      await bulkUploadProduct(uploadFile);
      showSuccessToast("Bulk upload successful");
      setOpenUploadModal(false);
      fetchProducts();
    } catch (err) {
      showErrorToast("Bulk upload failed", "Error");
    }
  };

  return (
    <Box>
      <Typography
        variant="h5"
        sx={{ color: "#124F3E", fontWeight: "bold", mb: 3 }}
      >
        Manage Products
      </Typography>

      {(error || permissionsError) && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error || permissionsError}
        </Alert>
      )}

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <TextField
          placeholder="Search"
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            setPage(0);
          }}
          sx={{ width: "300px" }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search size={20} />
              </InputAdornment>
            ),
            sx: { height: "40px" },
          }}
        />
        <Box sx={{ display: "flex", gap: 2 }}>
          {can.createProduct() && (
            <Button
              variant="contained"
              startIcon={<Upload size={18} />}
              onClick={() => setOpenUploadModal(true)}
              sx={{
                fontWeight: "bold",
                bgcolor: "#124F3E",
                "&:hover": { backgroundColor: "#1a614d !important" },
              }}
            >
              Upload CSV
            </Button>
          )}
          {can.createProduct() && (
            <Button
              variant="contained"
              startIcon={<CirclePlus size={18} />}
              onClick={() => {
                setSelectedProduct(null);
                setOpenModal(true);
              }}
              sx={{
                fontWeight: "bold",
                bgcolor: "#124F3E",
                "&:hover": { backgroundColor: "#1a614d !important" },
              }}
            >
              Add Product
            </Button>
          )}
        </Box>
      </Box>

      <DataTable
        loading={loading || loadingPermissions}
        columns={columns}
        rows={filteredProducts}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        totalRows={total}
        onEdit={
          can.editProduct()
            ? (row) => {
                const productData = {
                  id: row.id,
                  code: row.code,
                  sku: row.sku,
                  name: row.name,
                  price: row.price,
                  product_group: row.productGroup,
                  product_group_name: row.productGroupName,
                  classification: row.classification,
                  strategy: row.strategy,
                  lead_time: row.leadTime,
                  coverage_profile: row.coverageProfile,
                  planning_cycle: row.planningCycle,
                  mrp_collector: row.mrpCollector,
                  rounding_value: row.roundingValue,
                  min_order_quantity: row.minOrderQuantity,
                  max_order_quantity: row.maxOrderQuantity,
                  category: row.category,
                  category_name: row.categoryName,
                  sub_category: row.subCategory,
                  sub_category_name: row.subCategoryName,
                };
                setSelectedProduct(productData);
                setOpenModal(true);
              }
            : undefined
        }
        onDelete={
          can.deleteProduct()
            ? (row) => {
                const productData = {
                  id: row.id,
                  name: row.name,
                };
                setProductToDelete(productData);
                setDeleteModalOpen(true);
              }
            : undefined
        }
        editIcon={<EditIcon />}
        deleteIcon={<DeleteIcon />}
        editTooltip="Edit"
        deleteTooltip="Delete"
        editDisabled={!can.editProduct()}
        deleteDisabled={!can.deleteProduct()}
        components={{
          Pagination: () => (
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          ),
        }}
      />

      {openModal && (can.createProduct() || can.editProduct()) && (
        <Modal
          open
          onClose={() => {
            setSelectedProduct(null);
            setOpenModal(false);
          }}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
        >
          <Box sx={modalStyle}>
            <ProductForm
              initialData={selectedProduct}
              onSubmit={handleSubmit}
              onCancel={() => {
                setSelectedProduct(null);
                setOpenModal(false);
              }}
            />
          </Box>
        </Modal>
      )}

      <DeleteConfirmationModal
        open={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setProductToDelete(null);
        }}
        onConfirm={handleDeleteProduct}
        title="Delete Product"
        message="Are you sure you want to delete this product?"
        itemName={productToDelete?.name}
      />
      <UploadModal
        open={openUploadModal}
        onClose={() => setOpenUploadModal(false)}
        onFileChange={handleFileChange}
        onUpload={handleCSVUpload}
      />
      <Toast />
    </Box>
  );
};

export default ProductList;
